import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import checkoutSeasonTickets from '@justpark/api/src/requests/checkoutSeasonTickets';
import loadSeasonTicketsRequest from '@justpark/api/src/requests/loadSeasonTickets';
import getRequestErrorMessageCode from '@justpark/api/src/helpers/getRequestErrorMessageCode';
import getRequestErrorData from '@justpark/api/src/helpers/getRequestErrorData';
import { SeasonTicketVendorNotFound } from '../../api/ApiErrorCodes';
import type { Location } from '../../components/Search/LocationDetails';
import type { ChallengeResponse } from '../../types';

const debug = require('debug')('jp:stores:forms:seasonTickets');

export const CHECKOUT = 'seasonTickets/CHECKOUT';
export const CHECKOUT_SUCCESS = 'seasonTickets/CHECKOUT_SUCCESS';
export const CHECKOUT_FAIL = 'seasonTickets/CHECKOUT_FAIL';

type State = {
  loaded: boolean,
  loading: boolean,
  location: ?Location,
  submitting: boolean,
  success: boolean,
  errors: any,
  error: any,
  errorMsg: ?string,
  bootstrapFail: boolean,
  vendorNotFound: boolean,
  ddcRequired: boolean,
  ddcErrorMessage: string,
  ddcErrorCode: string,
  challengeResponse: null | ChallengeResponse
};

export const initialState = {
  loaded: false,
  loading: false,
  location: null,
  submitting: false,
  success: false,
  errors: {}, // individual field errors
  error: false, // global form error from checkout step
  errorMsg: null,
  bootstrapFail: false,
  vendorNotFound: false,
  ddcRequired: false,
  ddcErrorMessage: '',
  ddcErrorCode: '',
  challengeResponse: null
};

const sliceName = 'seasonTicketForm';

export const checkout = createAsyncThunk(
  'seasonTicketForm/checkout',
  async (payload, { rejectWithValue, getState, extra: { jpApiClient } }) => {
    const { sessionId, ticketType } = payload;
    const { challengeResponse } = getState().seasonTicketForm;

    const sessionObject = sessionId
      ? {
          sessionId,
          challengeWindowSize: 'fullPage',
          returnUrl: `${window.location.origin}/3ds-challenge-return`
        }
      : null;

    try {
      const request = checkoutSeasonTickets(
        payload,
        sessionObject,
        challengeResponse
      );

      const { data: response } = await jpApiClient(request);
      const challengeReturned = !!response?.data?.jwt;

      if (challengeReturned) {
        debug('Received challenge response', response);
        const err = new Error('3DS2 Challenge required');
        err.data = response.data;

        return rejectWithValue(err);
      }

      return { ticketType };
    } catch (e) {
      return rejectWithValue(getRequestErrorData(e));
    }
  }
);

export const load = createAsyncThunk(
  `${sliceName}/loadSeasonTickets`,
  async (vendor, { extra: { jpApiClient }, rejectWithValue }) => {
    try {
      const request = loadSeasonTicketsRequest(vendor);
      const {
        data: { data }
      } = await jpApiClient(request);
      return data;
    } catch (e) {
      return rejectWithValue(getRequestErrorMessageCode(e));
    }
  }
);

const seasonTicketsSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    startDDC: (state: State): State => ({
      ...state,
      ddcRequired: true,
      submitting: true
    }),
    onDDCFailure: (state: State, action): State => ({
      ...state,
      ddcErrorCode: action.payload.error,
      ddcErrorMessage: action.payload.message,
      submitting: false,
      ddcRequired: false
    })
  },
  extraReducers: (builder) => {
    builder.addCase(load.pending, (state: State) => {
      debug(load.pending);
      return { ...state, loading: !state.loading };
    });
    builder.addCase(load.fulfilled, (state: State, action) => {
      debug(load.fulfilled, action.payload);

      return {
        ...state,
        bootstrap: action.payload,
        loading: !state.loading,
        loaded: !state.loaded,
        bootstrapFail: false
      };
    });
    builder.addCase(load.rejected, (state: State, action) => {
      debug(load.rejected, action);
      return {
        ...state,
        loading: !state.loading,
        bootstrapFail: true,
        vendorNotFound: action.payload.code === SeasonTicketVendorNotFound
      };
    });
    builder.addCase(checkout.pending, (state: State) => {
      debug(CHECKOUT);
      return {
        ...state,
        submitting: true,
        errorMsg: '',
        ddcRequired: false
      };
    });
    builder.addCase(checkout.fulfilled, (state: State, action) => {
      debug(CHECKOUT_SUCCESS);
      const { ticketType } = action.payload;

      if (
        __CLIENT__ &&
        ticketType !== 'multi_usage' &&
        ticketType !== 'resident_ticket'
      ) {
        window.location.href = '/dashboard/season-tickets/';
      }
      return {
        ...state,
        submitting: false,
        success: true,
        challengeResponse: null
      };
    });
    builder.addCase(checkout.rejected, (state: State, action) => {
      debug(CHECKOUT_FAIL, action.payload);

      if (action?.payload?.data?.jwt) {
        // 3DS2 Challenge response
        return {
          ...state,
          challengeResponse: action.payload.data
        };
      }

      return {
        ...state,
        submitting: false,
        challengeResponse: null,
        errors:
          action.payload &&
          action.payload.meta &&
          Object.keys(action.payload.meta).length
            ? action.payload.meta
            : {},
        errorMsg:
          action.payload &&
          action.payload.message &&
          !Object.keys(action.payload.meta).length
            ? action.payload.message
            : ''
      };
    });
  }
});

export const { startDDC, onDDCFailure } = seasonTicketsSlice.actions;

export default seasonTicketsSlice;

export const selectBuySeasonTicketDDCRequired = (state) =>
  state[seasonTicketsSlice.name].ddcRequired;

export const selectBuySeasonTicketDDCErrorMessage = (state) =>
  state[seasonTicketsSlice.name].ddcErrorMessage;

export const selectBuySeasonTicketDDCErrorCode = (state) =>
  state[seasonTicketsSlice.name].ddcErrorCode;

export const selectBuySeasonTicketChallengeResponse = (state) =>
  state[seasonTicketsSlice.name].challengeResponse;
